/**
 * The global search panel (M5.C5, `specs/M7-C5-search.md` §9).
 *
 * Drawn to the captured geometry: a **1318 × 751** surface at (61, 117) holding a chip strip
 * of `li`s 32 px tall at y 133, a quick-filter rail of 169 × 34 links at x 66 on a 38 px pitch,
 * and — with no term — `Saved Searches` and `Recent Searches`.
 *
 * Two things measured on monday that this reproduces deliberately:
 *   - **Enter does not navigate.** There is no results page; the panel is the whole feature
 *     (`[se] 05-results-page.json`). Enter opens the *active row* instead.
 *   - `Tags` and `Docs` are chips even on an account with no docs. They render disabled here
 *     rather than being hidden, so the strip keeps its measured widths.
 *
 * Keyboard: one roving `aria-activedescendant` across every rendered row in visual order,
 * wrapping; `Escape` closes and returns focus to the opener.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_IMPLEMENTED,
  SEARCH_CATEGORY_LABELS,
  SEARCH_QUICK_FILTERS,
  SEARCH_QUICK_FILTER_LABELS,
  type SearchCategoryId,
  type SearchQuickFilterId,
  type SearchResultsView,
  type SearchRowView,
  type SearchSource,
} from './types';

const DEBOUNCE_MS = 200;

export interface SearchPanelProps {
  source: SearchSource;
  onClose: () => void;
  /** Navigation is injected so the panel works in the fixture harness with no router. */
  onNavigate?: (href: string) => void;
  initialTerm?: string;
  initialCategory?: SearchCategoryId;
}

const KIND_ICON: Record<string, string> = {
  board: '▦',
  item: '▸',
  update: '💬',
  file: '📎',
  person: '◍',
};

export function SearchPanel({
  source,
  onClose,
  onNavigate,
  initialTerm = '',
  initialCategory = 'all',
}: SearchPanelProps) {
  const [term, setTerm] = useState(initialTerm);
  const [category, setCategory] = useState<SearchCategoryId>(initialCategory);
  const [quickFilters, setQuickFilters] = useState<SearchQuickFilterId[]>([]);
  const [results, setResults] = useState<SearchResultsView | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    let cancelled = false;
    void source
      .recent()
      .then((rows) => {
        if (!cancelled) setRecent(rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [source]);

  /** 200 ms debounce: a keystroke is not a query (§7). */
  useEffect(() => {
    const trimmed = term.trim();
    if (trimmed.length === 0) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      let cancelled = false;
      void source
        .query({ term: trimmed, category, quickFilters })
        .then((next) => {
          if (cancelled) return;
          setResults(next);
          setActive(0);
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
      return () => {
        cancelled = true;
      };
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, category, quickFilters, source]);

  /** Every rendered row, flattened in visual order — the list the arrow keys walk. */
  const flatRows = useMemo<SearchRowView[]>(
    () => (results?.groups ?? []).flatMap((group) => group.rows),
    [results],
  );

  const open = useCallback(
    (row: SearchRowView) => {
      void source.remember(term.trim()).catch(() => undefined);
      onClose();
      if (onNavigate) onNavigate(row.href);
      else if (typeof window !== 'undefined') window.location.assign(row.href);
    },
    [onClose, onNavigate, source, term],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (flatRows.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % flatRows.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + flatRows.length) % flatRows.length);
    } else if (event.key === 'Enter') {
      // monday's Enter does **not** navigate to a results page; ours opens the active row.
      event.preventDefault();
      const row = flatRows[active];
      if (row) open(row);
    }
  };

  const toggleFilter = (id: SearchQuickFilterId) =>
    setQuickFilters((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  let rowIndex = -1;

  return (
    <div
      className="ez-search-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Search everything"
      data-testid="search-panel"
      onKeyDown={onKeyDown}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="ez-search-panel__head">
        <ul className="ez-search-chips" role="tablist" aria-label="Search categories" data-testid="search-chips">
          {SEARCH_CATEGORIES.map((id) => {
            const implemented = SEARCH_CATEGORIES_IMPLEMENTED.includes(id);
            return (
              <li
                key={id}
                role="tab"
                tabIndex={0}
                aria-selected={category === id}
                aria-disabled={!implemented}
                className={`ez-search-chip${category === id ? ' is-selected' : ''}${implemented ? '' : ' is-disabled'}`}
                data-testid={`search-chip-${id}`}
                onClick={() => implemented && setCategory(id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (implemented) setCategory(id);
                  }
                }}
              >
                {SEARCH_CATEGORY_LABELS[id]}
              </li>
            );
          })}
        </ul>
        {/* Captured at 100 × 32; the picker itself is not captured, so the control is inert. */}
        <button
          type="button"
          className="ez-search-datefilter"
          data-testid="search-filter-by-date"
          disabled
          title="Filter by date is not available yet"
        >
          Filter by date
        </button>
      </div>

      <div className="ez-search-panel__input">
        <input
          ref={inputRef}
          type="search"
          value={term}
          placeholder="Search for anything..."
          aria-label="Search everything"
          aria-activedescendant={flatRows[active] ? `search-row-${active}` : undefined}
          data-testid="search-panel-input"
          onChange={(event) => setTerm(event.target.value)}
        />
        {loading ? <span className="ez-search-panel__spinner" aria-hidden="true" /> : null}
      </div>

      {term.trim().length === 0 ? (
        <div className="ez-search-panel__body" data-testid="search-panel-empty">
          {/*
            monday's left column is the five quick-filter links with **no heading** — the two
            headings (`Saved Searches`, `Recent Searches`) both belong to the right column, in
            that order, which is exactly the order its DOM prints them in
            (`discovery/ui-reference/search/INDEX.md`).
          */}
          <section className="ez-search-col">
            <div className="ez-search-quick" role="group" aria-label="Quick filters">
              {SEARCH_QUICK_FILTERS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`ez-search-quick__item${quickFilters.includes(id) ? ' is-on' : ''}`}
                  aria-pressed={quickFilters.includes(id)}
                  data-testid={`search-quick-${id}`}
                  onClick={() => toggleFilter(id)}
                >
                  {SEARCH_QUICK_FILTER_LABELS[id]}
                </button>
              ))}
            </div>
          </section>

          <section className="ez-search-col">
            <h2 className="ez-search-section">Saved Searches</h2>
            {/* Saving a search has no store (open question 388); monday's copy, verbatim. */}
            <p className="ez-search-hint" data-testid="search-saved-empty">
              Save searches for quick access. Just click the save button to the right of the search
              field.
            </p>

            <h2 className="ez-search-section ez-search-section--recent">Recent Searches</h2>
            {recent.length === 0 ? (
              <p className="ez-search-hint" data-testid="search-recent-empty">
                Nothing yet — a search you open a result from shows up here.
              </p>
            ) : (
              <div className="ez-search-recent" data-testid="search-recent">
                {recent.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="ez-search-recent__item"
                    onClick={() => setTerm(value)}
                  >
                    {value}
                  </button>
                ))}
                <button
                  type="button"
                  className="ez-search-recent__clear"
                  data-testid="search-recent-clear"
                  onClick={() => {
                    void source.clearRecent().then(() => setRecent([]));
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="ez-search-results" role="listbox" aria-label="Search results" data-testid="search-results">
          {(results?.groups ?? []).length === 0 && !loading ? (
            <p className="ez-search-hint" data-testid="search-no-results">
              No results for “{term.trim()}”.
            </p>
          ) : null}
          {(results?.groups ?? []).map((group) => (
            <section key={group.kind} className="ez-search-group" data-testid={`search-group-${group.kind}`}>
              <h2 className="ez-search-section">
                {group.label} <span className="ez-search-count">{group.total}</span>
              </h2>
              {group.rows.map((row) => {
                rowIndex += 1;
                const index = rowIndex;
                return (
                  <a
                    key={`${row.kind}:${row.id}`}
                    id={`search-row-${index}`}
                    href={row.href}
                    role="option"
                    aria-selected={index === active}
                    className={`ez-search-row${index === active ? ' is-active' : ''}`}
                    data-testid={`search-row-${row.kind}-${row.id}`}
                    onMouseEnter={() => setActive(index)}
                    onClick={(event) => {
                      // ⌘-click / middle-click keep the browser's own behaviour.
                      if (event.metaKey || event.ctrlKey || event.button !== 0) return;
                      event.preventDefault();
                      open(row);
                    }}
                  >
                    <span className="ez-search-row__icon" aria-hidden="true">
                      {KIND_ICON[row.kind] ?? '•'}
                    </span>
                    <span className="ez-search-row__text">
                      <span className="ez-search-row__title">{row.title || '(untitled)'}</span>
                      {row.boardName ? (
                        <span className="ez-search-row__crumb">{row.boardName}</span>
                      ) : null}
                    </span>
                    {row.snippet ? <span className="ez-search-row__snippet">{row.snippet}</span> : null}
                  </a>
                );
              })}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
